import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { CheckCircleIcon } from '@calendly/ui/components/icons';
import { BareItem, Item } from '@calendly/ui/components/menu';
import { colorStrokeFocus } from '@calendly/ui/theme';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import moment from 'moment-timezone';
import { useEffect, useState, useMemo, useCallback, forwardRef } from 'react';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { useLazyOfferAvailabilityFetchAvailabilityQuery } from './api';
import { NEXT_3_DAYS, NEXT_5_DAYS, THIS_WEEK, NEXT_WEEK, SPECIFIC_DATES, DEFAULT_TIMEZONE, AVAILABILITY_OPTIONS_SEND_VIA_CALENDLY_STEP } from './constants';
import { SpecificDates } from './specific-dates';
import { formattedAvailableDays, getAvailabilityDateRange, getTimeSlotsForDays, hasThisWeekAvailability, hasNextWeekAvailability } from './utils';
const containerCss = "c1yudvh5";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const dropdownMenuCss = "d23q49x";
const DropdownMenu = forwardRef((props, ref) => <ViewportDropdownMenu ref={ref} {...props} className={classNames(dropdownMenuCss, props.className)} />);
const alignRightCss = "at8cwmm";
const AlignRight = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(alignRightCss, props.className)} />;
});
const AvailabilityOptionItem = ({
  value,
  name,
  isDisabled,
  currentAvailabilityOption,
  showSelectedState,
  onOptionChange,
  isSendFromCalendly
}) => {
  let ItemComponent;
  if (value === SPECIFIC_DATES) {
    ItemComponent = BareItem; // BareItem is needed since Item automatically closes the menu
  } else {
    ItemComponent = Item;
  }
  return <ItemComponent disabled={isDisabled} data-testid={`option-${value}`} onClick={() => onOptionChange(value)}>
      {name}
      {!isSendFromCalendly && showSelectedState && currentAvailabilityOption === value && <AlignRight>
          <CheckCircleIcon color={colorStrokeFocus} />
        </AlignRight>}
    </ItemComponent>;
};
export const OfferAvailabilityDropdown = ({
  eventTypeId,
  nextAvailability,
  timezone = DEFAULT_TIMEZONE,
  availabilityOption = NEXT_3_DAYS,
  onAvailabilityChange = () => {},
  onAvailabilitySelect,
  showSelectedState = true,
  ariaLabelledBy,
  disabled = false,
  loading = false,
  buttonComponent,
  showAbove = false,
  checkDisabledOptions = false,
  trackAnalytics = () => {},
  contact = null,
  source,
  isSendFromCalendly = false
}) => {
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const [isSpecificDatesOpen, setSpecificDatesOpen] = useState(false);
  const [currentAvailabilityOption, setCurrentAvailabilityOption] = useState(availabilityOption);
  const currentMonth = moment().tz(timezone).format('YYYY-MM');
  const [currentMonthId, setCurrentMonthId] = useState(currentMonth);
  const track = useTrack();
  const [fetchAvailability, {
    data: availabilityData,
    error: availabilityError,
    isFetching,
    isSuccess
  }] = useLazyOfferAvailabilityFetchAvailabilityQuery();
  const [fetchDisabledOptionsData, {
    data: disabledOptionsData,
    isSuccess: isDisabledOptionsSuccess
  }] = useLazyOfferAvailabilityFetchAvailabilityQuery();
  const AVAILABILITY_OPTIONS = useMemo(() => ({
    [NEXT_3_DAYS]: I18n.t('publisher.scheduling.offer_time_slots_modal.availability_options.next_3_days'),
    [NEXT_5_DAYS]: I18n.t('publisher.scheduling.offer_time_slots_modal.availability_options.next_5_days'),
    [THIS_WEEK]: I18n.t('publisher.scheduling.offer_time_slots_modal.availability_options.this_week'),
    [NEXT_WEEK]: I18n.t('publisher.scheduling.offer_time_slots_modal.availability_options.next_week'),
    [SPECIFIC_DATES]: I18n.t('publisher.scheduling.offer_time_slots_modal.availability_options.specific_dates')
  }), []);
  const handleClick = () => {
    toggleDropdown();
    if (!isExpanded && isSendFromCalendly) {
      trackAnalytics();
    }
  };
  const disabledOptions = useMemo(() => {
    if (!checkDisabledOptions) {
      return new Set();
    }
    if (!isDisabledOptionsSuccess || !disabledOptionsData || isEmpty(disabledOptionsData)) {
      return new Set();
    }
    const availableDays = formattedAvailableDays(disabledOptionsData);
    const disabledSet = new Set();
    if (!hasThisWeekAvailability(availableDays, timezone)) {
      disabledSet.add(THIS_WEEK);
    }
    if (!hasNextWeekAvailability(availableDays, timezone)) {
      disabledSet.add(NEXT_WEEK);
    }
    return disabledSet;
  }, [checkDisabledOptions, isDisabledOptionsSuccess, disabledOptionsData, timezone]);
  const createAvailabilityPayload = useCallback((option, availableDays, datesWithSpots, additionalData = {}) => ({
    availabilityOption: option,
    availableDays,
    datesWithSpots,
    availabilityData,
    isLoading: false,
    hasError: false,
    ...additionalData
  }), [availabilityData]);
  const {
    startDate,
    endDate
  } = getAvailabilityDateRange(nextAvailability, currentAvailabilityOption, currentMonthId);
  const trackSendViaCalendly = useCallback(step => {
    track({
      name: TRACKING_EVENT_NAMES.SEND_VIA_CALENDLY,
      properties: {
        event_type_id: eventTypeId?.toString(),
        contact_source: source?.toLowerCase(),
        contact_uuid: contact?.uuid ?? contact?.id,
        step: AVAILABILITY_OPTIONS_SEND_VIA_CALENDLY_STEP[step] ?? step
      }
    });
  }, [track, eventTypeId, source, contact]);
  useEffect(() => {
    if (checkDisabledOptions && eventTypeId && timezone) {
      const startOfThisWeek = moment().tz(timezone).startOf('week').format('YYYY-MM-DD');
      const endOfNextWeek = moment().tz(timezone).endOf('week').add(1, 'week').format('YYYY-MM-DD');
      fetchDisabledOptionsData({
        eventTypeId,
        startDate: startOfThisWeek,
        endDate: endOfNextWeek,
        timezone
      });
    }
  }, [checkDisabledOptions, fetchDisabledOptionsData, eventTypeId, timezone]);
  useEffect(() => {
    if (eventTypeId && startDate && endDate && timezone) {
      fetchAvailability({
        eventTypeId,
        startDate,
        endDate,
        timezone
      });
    }
  }, [fetchAvailability, eventTypeId, startDate, endDate, timezone]);
  useEffect(() => {
    if (currentAvailabilityOption === SPECIFIC_DATES) return;
    if (!isFetching && isSuccess && !isEmpty(availabilityData)) {
      const newAvailableDays = formattedAvailableDays(availabilityData);
      const timeSlotsForDays = getTimeSlotsForDays(currentAvailabilityOption, newAvailableDays);
      onAvailabilityChange(createAvailabilityPayload(currentAvailabilityOption, newAvailableDays, timeSlotsForDays));
    } else if (isFetching || availabilityError) {
      onAvailabilityChange(createAvailabilityPayload(currentAvailabilityOption, {}, {}, {
        availabilityData: null,
        isLoading: isFetching,
        hasError: !!availabilityError
      }));
    }
  }, [isFetching, isSuccess, availabilityData, availabilityError, currentAvailabilityOption, onAvailabilityChange, createAvailabilityPayload]);
  const handleSpecificDatesOption = useCallback(value => {
    setSpecificDatesOpen(true);
    setCurrentMonthId(currentMonth);
    if (isSendFromCalendly) {
      trackSendViaCalendly(value);
    }
  }, [currentMonth, isSendFromCalendly, trackSendViaCalendly]);
  const handleStandardOption = useCallback(value => {
    if (!showSelectedState && onAvailabilitySelect) {
      const newAvailableDays = availabilityData ? formattedAvailableDays(availabilityData) : {};
      const timeSlotsForDays = availabilityData ? getTimeSlotsForDays(value, newAvailableDays) : {};
      onAvailabilitySelect(createAvailabilityPayload(value, newAvailableDays, timeSlotsForDays));
    }
    if (isSendFromCalendly) {
      trackSendViaCalendly(value);
    }
    toggleDropdown();
  }, [showSelectedState, onAvailabilitySelect, availabilityData, createAvailabilityPayload, toggleDropdown, isSendFromCalendly, trackSendViaCalendly]);
  const handleAvailabilityOptionChange = useCallback(value => {
    setCurrentAvailabilityOption(value);
    if (value === SPECIFIC_DATES) {
      handleSpecificDatesOption(value);
    } else {
      handleStandardOption(value);
    }
  }, [handleSpecificDatesOption, handleStandardOption]);
  const handleClose = useCallback(() => {
    setSpecificDatesOpen(false);
  }, []);
  const handleSpecificDatesApply = useCallback((availableDays, datesWithSpots) => {
    const payload = createAvailabilityPayload(SPECIFIC_DATES, availableDays, datesWithSpots);
    if (!showSelectedState && onAvailabilitySelect) {
      onAvailabilitySelect(payload);
    }
    if (isSendFromCalendly) {
      trackSendViaCalendly('insert_specific_dates');
    }
    onAvailabilityChange(payload);
  }, [showSelectedState, onAvailabilitySelect, onAvailabilityChange, createAvailabilityPayload, trackSendViaCalendly, isSendFromCalendly]);
  useEffect(() => {
    if (!isExpanded) {
      setSpecificDatesOpen(false);
    }
  }, [isExpanded]);
  useEffect(() => {
    setCurrentAvailabilityOption(availabilityOption);
  }, [availabilityOption]);
  const ButtonComponent = buttonComponent;
  return <Container>
      <ButtonComponent onClick={handleClick} isExpanded={isExpanded} currentOption={currentAvailabilityOption} disabled={disabled} loading={loading} availabilityOptions={AVAILABILITY_OPTIONS} ariaLabelledBy={ariaLabelledBy} />
      <Dropdown>
        <DropdownMenu pullTop={showAbove}>
          {!isSpecificDatesOpen && <>
              {Object.entries(AVAILABILITY_OPTIONS).map(([value, name]) => {
            const isDisabled = disabledOptions.has(value);
            return <AvailabilityOptionItem key={value} value={value} name={name} isDisabled={isDisabled} currentAvailabilityOption={currentAvailabilityOption} showSelectedState={showSelectedState} onOptionChange={handleAvailabilityOptionChange} isSendFromCalendly={isSendFromCalendly} />;
          })}
            </>}
          {isSpecificDatesOpen && <SpecificDates timezone={timezone} currentMonthId={currentMonthId} setCurrentMonthId={setCurrentMonthId} isLoading={isFetching} availabilityData={availabilityData} onClose={handleClose} onApply={handleSpecificDatesApply} isSendFromCalendly={isSendFromCalendly} />}
        </DropdownMenu>
      </Dropdown>
    </Container>;
};

require("./offer-availability-dropdown.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./offer-availability-dropdown.js");